import { operations } from "../types/openapi";
import { api } from "./api";
import { ENDPOINTS } from "./endpoints";
import { TRequestValues } from "./authClient/auth/api-utils";
import { WalletEntity } from "interfaces";

type getAllCompaniesOperation = operations["getAllCompanies"];
type getCompanyByIdOperation = operations["getCompanyById"] & { wallets: string[] };
type blockCompanyOperation = operations["blockCompany"];
type unblockCompanyOperation = operations["unblockCompany"];
type activateCompanyOperation = operations["activateCompany"];
type updateCompanyOperation = operations["updateCompany"];
type createNewCompanyOperation = operations["createNewCompany"];
type getAllUsersByCompanyOperation = operations["getAllUsersByCompany"];
type getAllCompaniesByUserIdOperation = operations["getAllCompaniesByUserId"];
export type attachUserToCompanyOperation = operations["attachUserToCompany"];
type removeUserFromCompanyOperation = operations["detachUserFromCompany"];
type createBeneficiaryOperation = operations["createBeneficiary"];

/**
 * Грид компаний (доступно для OPERATOR)
 */
export const getCompanyList = (
  params: Record<string, TRequestValues>
): Promise<getAllCompaniesOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.companies(), params);
};

/**
 * Карточка компании (доступно для CUSTOMER, OPERATOR, LIQUIDITY_PROVIDER, COMPANY_DOC_UPLOADING)
 */
export const getCompanyById = ({
  id,
}: getCompanyByIdOperation["parameters"]["path"]): Promise<
  getCompanyByIdOperation["responses"]["200"]["content"]["*/*"] & { wallets: WalletEntity[] }
> => {
  return api.get(ENDPOINTS.companyById(id));
};

/**
 * Грид пользователей для компании (OPERATOR)
 */
export const getCompanyUserList = (
  { companyId }: getAllUsersByCompanyOperation["parameters"]["path"],
  params: Record<string, TRequestValues>
): Promise<getAllUsersByCompanyOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.companyUsers(companyId), params);
};

/**
 * Создать новую организацию (доступно для OPERATOR)
 */
export const createCompany = (
  formData: createNewCompanyOperation["requestBody"]["content"]["application/json"]
): Promise<string> => {
  return api.post(ENDPOINTS.companies(), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Обновление данных компании (доступно для OPERATOR)
 */
export const editCompany = (
  { id }: updateCompanyOperation["parameters"]["path"],
  formData: updateCompanyOperation["requestBody"]["content"]["application/json"]
): Promise<updateCompanyOperation["responses"]["200"]["content"]> => {
  return api.put(ENDPOINTS.editCompanyById(id), formData, {
    contentType: "multipart/form-data",
  });
};

/**
 * Блокировка компании (доступно для OPERATOR)
 */
export const blockCompany = ({
  id,
}: blockCompanyOperation["parameters"]["path"]): Promise<blockCompanyOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.blockCompany(id));
};

/**
 * Разблокировка компании (доступно для OPERATOR)
 */
export const unblockCompany = ({
  id,
}: unblockCompanyOperation["parameters"]["path"]): Promise<unblockCompanyOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.unblockCompany(id));
};

/**
 * Активация компании (доступно для OPERATOR)
 */
export const activateCompany = ({
  id,
}: activateCompanyOperation["parameters"]["path"]): Promise<
  activateCompanyOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.activateCompany(id));
};

/**
 * Подлкючить компанию к пользователю (доступен для OPERATOR)
 */
export const connectCompanyAndUser = (
  { id, userId }: attachUserToCompanyOperation["parameters"]["path"],
  roles: attachUserToCompanyOperation["requestBody"]["content"]["application/json"]
): Promise<attachUserToCompanyOperation["responses"]["200"]["content"]> => {
  return api.post(ENDPOINTS.connectCompanyAndUser(id, userId), { roles: roles });
};

/**
 * Список компаний пользователя
 */
export const getAllCompaniesByUserId = (
  { userId }: getAllCompaniesByUserIdOperation["parameters"]["path"],
  params: Record<string, TRequestValues>
): Promise<getAllCompaniesByUserIdOperation["responses"]["200"]["content"]["*/*"]> => {
  return api.get(ENDPOINTS.userCompanies(userId), params);
};

/**
 * Удаление пользователя из компании (доступно для OPERATOR)
 */
export const removeUserFromCompany = ({
  id,
  userId,
}: removeUserFromCompanyOperation["parameters"]["path"]): Promise<
  removeUserFromCompanyOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.removeUserFromCompany(id, userId));
};

/**
 * Создать номинальный счет для компании (доступно для OPERATOR)
 */
export const createBeneficiary = ({
  id,
}: createBeneficiaryOperation["parameters"]["path"]): Promise<
  createBeneficiaryOperation["responses"]["200"]["content"]
> => {
  return api.post(ENDPOINTS.createBeneficiary(id));
};
